import React, { useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { platformApi } from "../../lib/api";
import RichTextEditor from "../marketing/RichTextEditor";

export const HELP_CATEGORIES = [
  "company setup", "staff", "jobs", "estimates and invoices", "dispatch",
  "payments", "voice ai", "automations", "pipeline", "diagnostic guru",
  "email and messaging", "general",
];

const labelize = (c) => c.replace(/\b\w/g, (m) => m.toUpperCase());

export default function HelpArticleEditorModal({ article, onClose, onSaved }) {
  const isEdit = !!article?.id;
  const [title, setTitle] = useState(article?.title || "");
  const [category, setCategory] = useState(article?.category || "general");
  const [content, setContent] = useState(article?.content || "");
  const [status, setStatus] = useState(article?.status || "draft");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await platformApi.helpCenter.updateArticle(article.id, { title, category, content, status });
        toast.success("Article updated");
      } else {
        await platformApi.helpCenter.createArticle({ title, category, content, status });
        toast.success("Article created");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save article");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="help-article-editor-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-[#0C111B] border border-white/10 rounded-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-[16px] font-bold text-white">{isEdit ? "Edit Article" : "New Article"}</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-white/5" data-testid="help-article-editor-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-[11.5px] font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. How to create and send an estimate"
              data-testid="help-article-title-input"
              className="w-full h-10 px-3 rounded-lg bg-[#0F1623] border border-white/10 text-[13px] text-slate-200 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
            />
          </div>

          <div>
            <label className="block text-[11.5px] font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-testid="help-article-category-select"
              className="w-full h-10 px-2 rounded-lg bg-[#0F1623] border border-white/10 text-[13px] text-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500/30"
            >
              {HELP_CATEGORIES.map((c) => (
                <option key={c} value={c} className="bg-[#0F1623]">{labelize(c)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[11.5px] font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">Body</label>
            <RichTextEditor value={content} onChange={setContent} placeholder="Write the article content..." />
          </div>

          <div className="flex items-center justify-between rounded-lg border border-white/10 bg-[#0F1623] px-3.5 py-3">
            <div>
              <div className="text-[13px] font-medium text-slate-200">Published</div>
              <div className="text-[11.5px] text-slate-500 mt-0.5">Only published articles are used by the Help &amp; Support chatbot. Drafts are saved but not served.</div>
            </div>
            <button
              type="button"
              onClick={() => setStatus((s) => (s === "published" ? "draft" : "published"))}
              data-testid="help-article-status-toggle"
              className={`relative h-6 w-11 rounded-full shrink-0 ml-3 transition-colors ${status === "published" ? "bg-emerald-500" : "bg-slate-600"}`}
            >
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${status === "published" ? "translate-x-5" : "translate-x-0.5"}`} />
            </button>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="h-9 px-4 rounded-md border border-white/10 text-slate-300 text-[13px]" data-testid="help-article-cancel-btn">
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="help-article-save-btn"
              className="h-9 px-4 rounded-md bg-cyan-600 hover:bg-cyan-500 text-white text-[13px] font-semibold disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
